import {
    Decoration,
    DecorationSet,
    EditorView,
    ViewPlugin,
    ViewUpdate,
} from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";

import {
    LogSearchFieldNames,
    LogSearchFunctionNames,
} from "./schema.js";

const FieldNameSet = new Set<string>(LogSearchFieldNames);
const FunctionNameSet = new Set<string>(LogSearchFunctionNames);

type TokenMatch = {
    from: number;
    to: number;
    className: string;
};

const TokenPattern = /@(?:string|text|role|channel|user|number)::"[^"]*"|"[^"]*"|\[\*\]|>=|<=|==|!=|>|<|\b(?:and|or|not|in|eq|ne|gt|ge|lt|le)\b|[a-zA-Z_][a-zA-Z0-9_.]*(?=\()|[a-zA-Z_][a-zA-Z0-9_.]*(?:\[\d+]|\[\*])?(?:\.[a-zA-Z_][a-zA-Z0-9_]*(?:\[\d+]|\[\*])?)*|[(){}*,]/g;

function getTokenClass(token: string) {
    if (token.startsWith("@"))
        return "cm-logsearch-placeholder-token";

    if (/^"[^"]*"$/.test(token))
        return "cm-logsearch-string";

    if (/^(and|or|not|in|eq|ne|gt|ge|lt|le)$/.test(token))
        return "cm-logsearch-keyword";

    if (/^(>=|<=|==|!=|>|<)$/.test(token))
        return "cm-logsearch-operator";

    if (/^[(){}*,]$/.test(token) || token === "[*]")
        return "cm-logsearch-punctuation";

    const functionName = token.endsWith("(")
        ? token.slice(0, -1)
        : token;

    if (FunctionNameSet.has(functionName))
        return "cm-logsearch-function";

    if (FieldNameSet.has(token) || token.startsWith("extra."))
        return "cm-logsearch-field";

    return "cm-logsearch-identifier";
}

function collectTokens(text: string): TokenMatch[] {
    const tokens: TokenMatch[] = [];
    let match: RegExpExecArray | null;

    TokenPattern.lastIndex = 0;

    while ((match = TokenPattern.exec(text))) {
        const token = match[0]!;

        tokens.push({
            from: match.index,
            to: match.index + token.length,
            className: getTokenClass(token),
        });
    }

    return tokens;
}

function buildDecorations(view: EditorView): DecorationSet {
    const builder = new RangeSetBuilder<Decoration>();
    const text = view.state.doc.toString();

    for (const token of collectTokens(text)) {
        builder.add(
            token.from,
            token.to,
            Decoration.mark({ class: token.className }),
        );
    }

    return builder.finish();
}

export const logSearchHighlightExtension = ViewPlugin.fromClass(class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
        this.decorations = buildDecorations(view);
    }

    update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged)
            this.decorations = buildDecorations(update.view);
    }
}, {
    decorations: (plugin) => plugin.decorations,
});
